/**
 * wayai org <create|pull|push|diff> — manage organizations and org-scoped skills.
 *
 *   wayai org create "<name>" — create a new organization (you become its owner)
 *
 * The pull/push/diff subcommands sync ORG-scoped skills as code (the org-level
 * analog of hub skills, shared across hubs). They live in the org dir for the
 * active layout (`wayai-ws/org/`, or the legacy root-level `org/`), as
 * `skills.yaml` + `skills/<skill_name>/`; one org per repo (org id from
 * `wayai-ws/wayai.yaml`). Hub config (`wayai-ws/hubs/`) is untouched. An org dir an
 * older CLI wrote (`resources.yaml` + `resources/`) is converted by `org pull` and
 * refused by `org diff` and `org push`.
 *
 *   wayai org pull   — fetch org skills → <orgDir>/
 *   wayai org diff   — preview what `org push` would change on the platform
 *   wayai org push   — apply local org skills to the platform (fans out to linked hubs)
 *
 * `create` is git-agnostic (no repo/scope required — it's the first-org bootstrap);
 * pull/push/diff require a scoped repo.
 */

import { requireAuth } from '../lib/auth.js';
import { ApiClient } from '../lib/api-client.js';
import { requireRepoConfig } from '../lib/repo-config.js';
import { findGitRoot } from '../lib/workspace.js';
import { warnLayoutOnce } from '../lib/layout.js';
import { confirm } from '../lib/utils.js';
import { printDiff } from '../lib/diff-display.js';
import { extractApiMessage } from '../lib/errors.js';
import type { CiDiffResponse } from '../lib/types.js';
import {
  getOrgDir,
  orgManifestExists,
  parseOrgSkills,
  writeOrgSkills,
  downloadOrgBinaryFiles,
  assertOrgDirConvertible,
  ORG_MANIFEST_NAME,
} from '../lib/org-workspace.js';

interface OrgContext {
  orgId: string;
  orgDir: string;
  client: ApiClient;
}

function parseFlags(args: string[]): { autoConfirm: boolean } {
  return { autoConfirm: args.includes('--yes') || args.includes('-y') };
}

async function resolveContext(): Promise<OrgContext> {
  const { organization_id: orgId } = requireRepoConfig();
  const gitRoot = findGitRoot();
  if (!gitRoot) {
    console.error('Not inside a git repository.');
    process.exit(1);
  }
  warnLayoutOnce(gitRoot);
  const { config, accessToken } = await requireAuth();
  const client = new ApiClient({ apiUrl: config.api_url, accessToken });
  return { orgId, orgDir: getOrgDir(gitRoot), client };
}

export async function orgCommand(args: string[]): Promise<void> {
  const sub = args[0];
  const rest = args.slice(1);
  switch (sub) {
    case 'create':
      return orgCreate(rest);
    case 'pull':
      return orgPull(rest);
    case 'diff':
      return orgDiff(rest);
    case 'push':
      return orgPush(rest);
    default:
      console.error('Usage: wayai org <create|pull|push|diff> [-y]');
      process.exit(1);
  }
}

/** First non-flag token, skipping the value consumed by `--region`. */
function firstPositional(args: string[]): string | undefined {
  for (let i = 0; i < args.length; i++) {
    const a = args[i];
    if (a.startsWith('-')) {
      if (a === '--region') i++; // skip its value
      continue;
    }
    return a;
  }
  return undefined;
}

function getFlagValue(args: string[], flag: string): string | undefined {
  const idx = args.indexOf(flag);
  return idx !== -1 ? args[idx + 1] : undefined;
}

/**
 * wayai org create "<name>" [--region <region>] [--json]
 *
 * Creates an organization and makes the caller its owner. Deliberately does NOT
 * require a git repo or an org binding — this is the bootstrap step a brand-new
 * user runs before they have anything scoped. Follow with `wayai init`.
 */
async function orgCreate(args: string[]): Promise<void> {
  const json = args.includes('--json');
  const name = firstPositional(args);
  if (!name) {
    console.error('Usage: wayai org create "<name>" [--region <region>] [--json]');
    process.exit(1);
  }
  const region = getFlagValue(args, '--region');

  const { config, accessToken } = await requireAuth();
  const client = new ApiClient({ apiUrl: config.api_url, accessToken });

  if (!json) console.log(`Creating organization "${name}"...`);
  const { organization } = await client.createOrganization(name, region);

  if (json) {
    console.log(JSON.stringify(organization, null, 2));
    return;
  }
  console.log(`Created organization: ${organization.name} (${organization.id})`);
  if (organization.region) {
    const note = organization.region_explicit ? 'as requested' : 'closest to you — pass --region to override';
    console.log(`  Region: ${organization.region} (${note})`);
  }
  console.log(`\nScope a repo to it with \`wayai init --org ${organization.id}\`.`);
}

async function orgPull(args: string[]): Promise<void> {
  const { autoConfirm } = parseFlags(args);
  const { orgId, orgDir, client } = await resolveContext();

  console.log('Fetching organization skills...');
  const payload = await client.pullOrgSkills(orgId);

  // If org/ already has content, preview what pulling would overwrite locally. `'pull'` compares
  // every skill the organization holds, so a local section that names none still lists them
  // (a `'push'` comparison would read it as "nothing changes"). An org dir an older CLI wrote is
  // read as its skills, and converted below.
  // An org dir an older CLI wrote is converted below: anything it would remove unasked stops the
  // pull now, before the preview and its question.
  assertOrgDirConvertible(orgDir, payload);
  if (orgManifestExists(orgDir) && !autoConfirm) {
    const local = parseOrgSkills(orgDir, { readOldLayout: true });
    // A local file the platform refuses to compare (`enabled: no`) is one this pull repairs, so it
    // asks rather than stops, as `wayai pull` does. `-y` never compares.
    let diff: CiDiffResponse | undefined;
    try {
      diff = await client.diffOrgSkills(orgId, local, 'pull');
    } catch (err) {
      console.log('\nThe platform could not compare your local org/, so the changes cannot be listed:');
      console.log(`  ${extractApiMessage(err)}`);
    }
    if (diff?.has_changes) {
      console.log('\nLocal org/ differs from the platform. Pulling overwrites local with the platform state:');
      printDiff(diff.summary);
    }
    if (!diff || diff.has_changes) {
      const ok = await confirm('Overwrite local org/ with platform state?');
      if (!ok) {
        console.log('Cancelled.');
        return;
      }
    }
  }

  writeOrgSkills(orgDir, payload);
  await downloadOrgBinaryFiles(orgDir, payload);
  console.log(`Organization skills written to ${orgDir}`);
}

async function orgDiff(args: string[]): Promise<void> {
  void args;
  const { orgId, orgDir, client } = await resolveContext();
  if (!orgManifestExists(orgDir)) {
    console.error(`No ${orgDir}/${ORG_MANIFEST_NAME} found. Run \`wayai org pull\` first.`);
    process.exit(1);
  }
  const local = parseOrgSkills(orgDir);
  const diff = await client.diffOrgSkills(orgId, local, 'push');
  if (!diff.has_changes) {
    console.log('No changes — org/ matches the platform.');
    return;
  }
  console.log('Changes `wayai org push` would apply:');
  printDiff(diff.summary);
}

async function orgPush(args: string[]): Promise<void> {
  const { autoConfirm } = parseFlags(args);
  const { orgId, orgDir, client } = await resolveContext();
  if (!orgManifestExists(orgDir)) {
    console.error(`No ${orgDir}/${ORG_MANIFEST_NAME} found. Run \`wayai org pull\` first.`);
    process.exit(1);
  }

  const local = parseOrgSkills(orgDir);
  const diff = await client.diffOrgSkills(orgId, local, 'push');

  if (!diff.has_changes) {
    console.log('No changes — org/ already matches the platform.');
    return;
  }

  console.log('Changes to apply:');
  printDiff(diff.summary);

  if (!autoConfirm) {
    const ok = await confirm('Apply these changes to the organization?');
    if (!ok) {
      console.log('Cancelled.');
      return;
    }
  }

  const result = await client.pushOrgSkills(orgId, local);
  const c = result.changes;
  console.log(
    `Pushed skills: ${c.resources_created} created, ${c.resources_updated} updated, ${c.resources_deleted} deleted; ` +
      `${c.resource_files_uploaded} file(s) uploaded, ${c.resource_files_deleted} deleted.`,
  );
  if (result.fanout.hubs > 0) {
    const failed = result.fanout.failures > 0 ? ` (${result.fanout.failures} failed)` : '';
    console.log(`Synced to ${result.fanout.hubs} linked hub shadow(s)${failed}.`);
  }
  if (result.errors?.length) {
    console.warn('\nCompleted with errors:');
    for (const e of result.errors) console.warn(`  - ${e}`);
  }

  // Re-pull so server-assigned ids land in the local manifest (matches `wayai push`).
  const refreshed = await client.pullOrgSkills(orgId);
  writeOrgSkills(orgDir, refreshed);
  await downloadOrgBinaryFiles(orgDir, refreshed);
}
